/**
 * 纯 JS 加密实现（非安全上下文 fallback）
 *
 * 在 Web Crypto API 不可用时（HTTP 局域网等），使用 aes-js 库提供 AES-256-CBC 加密。
 * 算法：AES-256-CBC + HMAC-SHA256，与 crypto-native.ts 完全兼容。
 */

import aesjs from 'aes-js';

// ────────── 常量 ──────────

const IV_LENGTH = 16;
const KEY_HEX_LENGTH = 64;

// ────────── 工具函数 ──────────

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function validateKeyHex(keyHex: string): void {
  if (!keyHex || keyHex.length !== KEY_HEX_LENGTH) {
    throw new Error(
      `[Crypto-JS] 密钥格式错误：需要 ${KEY_HEX_LENGTH} 个字符的 Hex 字符串，当前长度 ${keyHex?.length || 0}`,
    );
  }
}

function generateRandomIV(): Uint8Array {
  const iv = new Uint8Array(IV_LENGTH);
  crypto.getRandomValues(iv);
  return iv;
}

// ────────── SHA-256 ──────────

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function sha256Bytes(data: Uint8Array): Uint8Array {
  const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
  const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
  const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
  const sigma0 = (x: number) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
  const sigma1 = (x: number) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
  const gamma0 = (x: number) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
  const gamma1 = (x: number) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);

  const bitLen = data.length * 8;
  const padded = new Uint8Array(Math.ceil((data.length + 9) / 64) * 64);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 4, bitLen, false);

  let [h0, h1, h2, h3, h4, h5, h6, h7] = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ];

  for (let offset = 0; offset < padded.length; offset += 64) {
    const W = new Array<number>(64);
    for (let t = 0; t < 16; t++) W[t] = view.getUint32(offset + t * 4, false);
    for (let t = 16; t < 64; t++)
      W[t] = (gamma1(W[t - 2]) + W[t - 7] + gamma0(W[t - 15]) + W[t - 16]) | 0;

    let [a, b, c, d, e, f, g, h] = [h0, h1, h2, h3, h4, h5, h6, h7];
    for (let t = 0; t < 64; t++) {
      const t1 = (h + sigma1(e) + ch(e, f, g) + SHA256_K[t] + W[t]) | 0;
      const t2 = (sigma0(a) + maj(a, b, c)) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  const result = new Uint8Array(32);
  const rv = new DataView(result.buffer);
  [h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => {
    rv.setUint32(i * 4, v, false);
  });
  return result;
}

// ────────── HMAC-SHA256 ──────────

function hmacSha256(data: Uint8Array, key: Uint8Array): Uint8Array {
  const blockSize = 64;
  const keyBlock = key.length > blockSize ? sha256Bytes(key) : key;

  const paddedKey = new Uint8Array(blockSize);
  paddedKey.set(keyBlock);

  const ipad = new Uint8Array(blockSize);
  const opad = new Uint8Array(blockSize);
  for (let i = 0; i < blockSize; i++) {
    ipad[i] = paddedKey[i] ^ 0x36;
    opad[i] = paddedKey[i] ^ 0x5c;
  }

  const inner = new Uint8Array(blockSize + data.length);
  inner.set(ipad);
  inner.set(data, blockSize);
  const innerHash = sha256Bytes(inner);

  const outer = new Uint8Array(blockSize + 32);
  outer.set(opad);
  outer.set(innerHash, blockSize);
  return sha256Bytes(outer);
}

// ────────── 导出 API（与 crypto-native.ts 接口一致） ──────────

/** AES-256-CBC 加密（使用 aes-js 库） */
export async function encryptAESFallback(
  plaintext: string,
  keyHex: string,
): Promise<string> {
  if (!plaintext) return plaintext;
  validateKeyHex(keyHex);

  const iv = generateRandomIV();
  const key = Array.from(hexToBytes(keyHex));
  const data = aesjs.padding.pkcs7.pad(
    Array.from(new TextEncoder().encode(plaintext)),
  );

  const aesCbc = new aesjs.ModeOfOperation.cbc(key, Array.from(iv));
  const encrypted = aesCbc.encrypt(data);

  const combined = new Uint8Array(IV_LENGTH + encrypted.length);
  combined.set(iv, 0);
  combined.set(encrypted, IV_LENGTH);
  return btoa(String.fromCharCode(...combined));
}

/** AES-256-CBC 解密（使用 aes-js 库） */
export async function decryptAESFallback(
  encryptedBase64: string,
  keyHex: string,
): Promise<string> {
  if (!encryptedBase64) return encryptedBase64;
  validateKeyHex(keyHex);

  const combined = Uint8Array.from(atob(encryptedBase64), (c) =>
    c.charCodeAt(0),
  );
  if (combined.length < IV_LENGTH + 16) {
    throw new Error('[Crypto-JS] 加密数据长度不足');
  }

  const iv = Array.from(combined.slice(0, IV_LENGTH));
  const ciphertext = Array.from(combined.slice(IV_LENGTH));
  const key = Array.from(hexToBytes(keyHex));

  const aesCbc = new aesjs.ModeOfOperation.cbc(key, iv);
  const decrypted = aesjs.padding.pkcs7.strip(aesCbc.decrypt(ciphertext));
  return new TextDecoder().decode(new Uint8Array(decrypted));
}

/** HMAC-SHA256 签名 */
export async function generateSignatureFallback(
  data: string,
  keyHex: string,
): Promise<string> {
  const key = hexToBytes(keyHex);
  const dataBytes = new TextEncoder().encode(data);
  return bytesToHex(hmacSha256(dataBytes, key));
}

/** 验证 HMAC-SHA256 签名 */
export async function verifySignatureFallback(
  data: string,
  signature: string,
  keyHex: string,
): Promise<boolean> {
  if (!signature) return false;
  const expected = await generateSignatureFallback(data, keyHex);
  if (expected.length !== signature.length) return false;
  let result = 0;
  for (let i = 0; i < expected.length; i++) {
    result |= expected.charCodeAt(i) ^ signature.charCodeAt(i);
  }
  return result === 0;
}

/** SHA-256 哈希 */
export async function sha256Fallback(data: string): Promise<string> {
  const dataBytes = new TextEncoder().encode(data);
  return bytesToHex(sha256Bytes(dataBytes));
}
